import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextReactComponents } from "next-sanity";
import {
  asPortableText,
  hardBreakHandler,
  linkMarkHandler,
  onMissingPortableTextComponent,
  proseBaseClasses,
} from "./shared";

/**
 * Dedicated component for the `bodyCopyWithLinks` schema.
 *
 * Schema permits `normal` blocks; `strong` and `em` decorators; the `link`
 * annotation. No lists, no headings. The `as` prop selects a typography
 * preset.
 *
 * Text colour is NOT bundled into the mode — pass `text-text-primary` /
 * `text-text-secondary` via `className` at the call site. Link decoration
 * (`underline`, accent colour for CTA mode) stays in the mode because it's
 * schema-intrinsic — every link annotation rendered by this schema needs to
 * look like a link.
 */
export type RichTextBodyCopyWithLinksAs =
  | "body"
  | "body-lg"
  | "body-sm-flat"
  | "body-sm-medium-flat"
  | "cta";

const wrapperClasses: Record<RichTextBodyCopyWithLinksAs, string> = {
  body: "typography-body-sm-regular desktop:typography-body-md-regular [&_a]:underline",
  "body-lg":
    "typography-body-md-regular desktop:typography-body-lg-regular [&_a]:underline",
  "body-sm-flat": "typography-body-sm-regular [&_a]:underline",
  "body-sm-medium-flat": "typography-body-sm-medium [&_a]:underline",
  cta: "typography-body-sm-medium tablet:typography-body-md-medium [&_a]:underline [&_a]:text-text-link",
};

const components: Partial<PortableTextReactComponents> = {
  marks: {
    link: linkMarkHandler,
  },
  hardBreak: hardBreakHandler,
};

interface RichTextBodyCopyWithLinksProps<T = unknown> {
  richText?: T | null;
  className?: string;
  as?: RichTextBodyCopyWithLinksAs;
  compact?: boolean;
}

export function RichTextBodyCopyWithLinks<T = unknown>({
  richText,
  className,
  as = "body",
  compact,
}: RichTextBodyCopyWithLinksProps<T>) {
  if (!richText) return null;
  return (
    <div
      className={cn(
        proseBaseClasses,
        wrapperClasses[as],
        compact && "[&>p]:m-0",
        className,
      )}
    >
      <PortableText
        value={asPortableText(richText)}
        components={components}
        onMissingComponent={onMissingPortableTextComponent}
      />
    </div>
  );
}
